import { createContext, useContext, useRef } from "react";
import { type StoreApi, useStore } from "zustand";
import { createWidthGroupRegistry, type WidthGroupRegistry } from "../../lib/width-group/width-group-registry";
import type { TimeRange } from "./ChartSpec";
import { type CrosshairBus, createCrosshairBus } from "./crosshair/crosshair-bus";

import { createDataViewerStore, type DataViewerState } from "./data-viewer-store";

const DataViewerContext = createContext<StoreApi<DataViewerState> | null>(null);
const CrosshairBusContext = createContext<CrosshairBus | null>(null);
const WidthGroupRegistryContext = createContext<WidthGroupRegistry | null>(null);

export function DataViewerProvider({
    children,
    defaultTimeRange,
}: {
    children: React.ReactNode;
    defaultTimeRange?: TimeRange;
}) {
    const storeRef = useRef<StoreApi<DataViewerState> | null>(null);
    if (!storeRef.current) {
        storeRef.current = createDataViewerStore(defaultTimeRange);
    }

    const crosshairBusRef = useRef<CrosshairBus | null>(null);
    if (!crosshairBusRef.current) {
        crosshairBusRef.current = createCrosshairBus();
    }

    const widthGroupRegistryRef = useRef<WidthGroupRegistry | null>(null);
    if (!widthGroupRegistryRef.current) {
        widthGroupRegistryRef.current = createWidthGroupRegistry();
    }

    const store = storeRef.current;
    const crosshairBus = crosshairBusRef.current;
    const widthGroupRegistry = widthGroupRegistryRef.current;

    return (
        <DataViewerContext.Provider value={store}>
            <CrosshairBusContext.Provider value={crosshairBus}>
                <WidthGroupRegistryContext.Provider value={widthGroupRegistry}>
                    {children}
                </WidthGroupRegistryContext.Provider>
            </CrosshairBusContext.Provider>
        </DataViewerContext.Provider>
    );
}

export function useDataViewerStore<T>(selector: (s: DataViewerState) => T) {
    const store = useContext(DataViewerContext);
    if (!store) {
        throw new Error("useDataViewerStore must be used within a DataViewerProvider");
    }
    return useStore(store, selector);
}

export function useCrosshairBus(): CrosshairBus {
    const ctx = useContext(CrosshairBusContext);
    if (!ctx) {
        throw new Error("useCrosshairBus must be used within a DataViewerProvider");
    }
    return ctx;
}

export function useWidthGroupRegistry(): WidthGroupRegistry {
    const ctx = useContext(WidthGroupRegistryContext);
    if (!ctx) {
        throw new Error("useWidthGroupRegistry must be used within a DataViewerProvider");
    }
    return ctx;
}
